import { useCallback, useState } from 'react';
import type { ColumnSortDirection } from './useColumnSort';

/**
 * CR-8667 / CR-8668 — sort state for a paginated table sorted on the server.
 *
 * Lifted out of the Supplier Performance table (CR-8667) so the POs Late to
 * Raise table (CR-8668) sorts the same way. A header click cycles ascending →
 * descending → the table's default order, and the sort goes to the server as
 * `?sort=<field>` / `?sort=-<field>`, which applies it to every row before the
 * page is cut. Sorting only the rows on screen would rank one page.
 *
 * Pair it with {@link SortableTh} for the header and its caret. For a table
 * that holds every row in the browser, use `useColumnSort` instead.
 */

/** The active column (the row's serialized field name) and its direction. */
export interface ServerColumnSort {
  field: string;
  direction: ColumnSortDirection;
}

/**
 * The state after clicking `field`: a new column starts ascending, the active
 * one turns descending, and a third click clears the sort.
 *
 * @param current - the sort in effect, or null for the default order
 * @param field - the header clicked
 * @returns the next sort, or null for the default order
 */
export function nextSort(current: ServerColumnSort | null, field: string): ServerColumnSort | null {
  if (!current || current.field !== field) return { field, direction: 'asc' };
  if (current.direction === 'asc') return { field, direction: 'desc' };
  return null;
}

/**
 * The `?sort=` value for a sort, e.g. `-last_price`.
 *
 * @param sort - the sort in effect
 * @returns the request value, or undefined for the default order
 */
export function sortParam(sort: ServerColumnSort | null): string | undefined {
  if (!sort) return undefined;
  return `${sort.direction === 'desc' ? '-' : ''}${sort.field}`;
}

/**
 * Sort state for a server-sorted table.
 *
 * @example
 * const { sort, onSort } = useServerColumnSort(() => setPage(1));
 * api.list({ ...filters, sort: sortParam(sort), page });
 *
 * @param onChange - called after every header click, e.g. to return to page 1
 * @returns the sort in effect (null = default order) and the header click handler
 */
export function useServerColumnSort(onChange?: () => void) {
  const [sort, setSort] = useState<ServerColumnSort | null>(null);
  const onSort = useCallback((field: string) => {
    setSort((current) => nextSort(current, field));
    onChange?.();
  }, [onChange]);
  return { sort, onSort };
}
